import { useEffect, useRef } from 'react';

/**
 * Google AdSense ad unit for a React SPA.
 *
 * The AdSense loader script is already added by public/index.html (deferred load).
 * This component renders a fresh <ins> tag on every mount and pushes to
 * window.adsbygoogle exactly once. It survives:
 *   - React StrictMode double-mount (guarded by a ref)
 *   - SPA route changes (new <ins> per route)
 *   - Slow AdSense script (retries every 400ms up to 20s until adsbygoogle is defined)
 *
 * Placement: use inside main content flow. Do NOT place above-the-fold hero
 * or below every card — Google penalises over-monetised layouts.
 *
 * Publisher ID is intentionally hardcoded here — same value as the loader in
 * index.html — so accidental mismatch is impossible.
 */
const ADSENSE_CLIENT = 'ca-pub-3278670821504977';

export const AdUnit = ({
  slot,
  format = 'auto',
  fullWidthResponsive = true,
  style,
  className = '',
  label = 'Advertisement',
}) => {
  const insRef = useRef(null);
  const pushedRef = useRef(false);

  useEffect(() => {
    if (pushedRef.current) return;
    pushedRef.current = true;

    let attempts = 0;
    const tryPush = () => {
      if (typeof window === 'undefined') return;
      if (!insRef.current) return;
      // Only push if adsbygoogle is loaded AND this <ins> hasn't been filled yet
      if (window.adsbygoogle && insRef.current.getAttribute('data-adsbygoogle-status') !== 'done') {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {
          // AdSense throws if the same ins is pushed twice — safe to ignore
          console.warn('AdSense push skipped:', e?.message);
        }
        return;
      }
      // Retry until the deferred loader kicks in (max ~20s)
      if (attempts < 50) {
        attempts += 1;
        setTimeout(tryPush, 400);
      }
    };
    tryPush();
  }, []);

  return (
    <div
      className={`ad-unit-wrapper w-full my-6 text-center ${className}`}
      data-testid={`ad-unit-${slot}`}
    >
      <div className="font-outfit text-[10px] uppercase tracking-wider text-gray-400 mb-1">
        {label}
      </div>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: 'block', minHeight: 90, ...style }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={fullWidthResponsive ? 'true' : 'false'}
      />
    </div>
  );
};
